/**
 * Questionnaire return contract (AC-008, AC-036, AC-058):
 *   /project/:pid/launch?intent_goal=<goal>&intent_channels=a,b&intent_pa=a,b&intent_platform=<p>
 * Values come from the dogfood funnel's option ids via `@variable.label` substitution
 * (V2VariableResolver: `.value` resolves to the option's opaque internal id, `.label` resolves
 * to the option's semantic value — populated from the screen item's `variableValue`, i.e. our
 * contract vocabulary such as "first_value"/"facebook"); the web-shell joins multi-select values
 * with ", ". Legacy option ids (validate/ads_ready/
 * full_production/ads/google/none) map onto the contract vocabulary here — never on the server.
 *
 * AM-6 (binding controller ruling): 'ads' and 'full_production' are legacy single-answer goal
 * ids from an earlier questionnaire revision that did not ask about channels separately, so
 * they carry an implied default channel set (both paid channels) — used ONLY when the URL does
 * not carry an `intent_channels` key at all; an explicit (even empty) `intent_channels` always
 * wins.
 */
import type { ActivationIntent, IntentChannel, IntentGoal, IntentPlatform, IntentProductAnalytics } from '@modules/shared-types';
import { INTENT_CHANNELS, INTENT_PLATFORMS, INTENT_PRODUCT_ANALYTICS } from '@modules/shared-types';

export const INTENT_PARAM_KEYS = ['intent_goal', 'intent_channels', 'intent_pa', 'intent_platform'] as const;

const GOAL_ALIASES: Record<string, IntentGoal> = {
  first_value: 'first_value', validate: 'first_value', ads_ready: 'first_value',
  sell: 'sell', monetized: 'sell',
};
/** AM-6: legacy single-answer goal ids that imply a default channel set. */
const LEGACY_GOAL_WITH_DEFAULT_CHANNELS: Record<string, { goal: IntentGoal; defaultChannels: IntentChannel[] }> = {
  ads: { goal: 'first_value', defaultChannels: ['facebook', 'tiktok'] },
  full_production: { goal: 'sell', defaultChannels: ['facebook', 'tiktok'] },
};
const CHANNEL_ALIASES: Record<string, IntentChannel> = { facebook: 'facebook', meta: 'facebook', tiktok: 'tiktok', google: 'google_ads', google_ads: 'google_ads', organic: 'organic' };
const PA_ALIASES: Record<string, IntentProductAnalytics | null> = { ga4: 'ga4', mixpanel: 'mixpanel', amplitude: 'amplitude', posthog: 'posthog', none: null };

export type ParsedIntentParams = { present: false } | { present: true; intent: Omit<ActivationIntent, 'updatedAt'> | null };

function splitList(raw: string | null): string[] {
  return (raw ?? '').split(/[\s,]+/).map((v) => v.trim().toLowerCase()).filter(Boolean);
}

function parseChannels(raw: string | null): IntentChannel[] | null {
  const channels: IntentChannel[] = [];
  for (const value of splitList(raw)) {
    const channel = CHANNEL_ALIASES[value];
    if (!channel || !(INTENT_CHANNELS as readonly string[]).includes(channel)) return null;
    if (!channels.includes(channel)) channels.push(channel);
  }
  return channels;
}

export function parseIntentParams(search: string): ParsedIntentParams {
  const params = new URLSearchParams(search);
  if (!INTENT_PARAM_KEYS.some((key) => params.has(key))) return { present: false };

  const rawGoal = (params.get('intent_goal') ?? '').trim().toLowerCase();
  let goal: IntentGoal | undefined;
  let defaultChannels: IntentChannel[] | undefined;
  if (rawGoal === '') {
    goal = 'first_value';
  } else if (rawGoal in LEGACY_GOAL_WITH_DEFAULT_CHANNELS) {
    const legacy = LEGACY_GOAL_WITH_DEFAULT_CHANNELS[rawGoal];
    goal = legacy.goal;
    if (!params.has('intent_channels')) defaultChannels = legacy.defaultChannels;
  } else {
    goal = GOAL_ALIASES[rawGoal];
  }
  if (!goal) return { present: true, intent: null };

  const channels = defaultChannels ?? parseChannels(params.get('intent_channels'));
  if (channels === null) return { present: true, intent: null };

  const productAnalytics: IntentProductAnalytics[] = [];
  for (const raw of splitList(params.get('intent_pa'))) {
    if (!(raw in PA_ALIASES)) return { present: true, intent: null };
    const pa = PA_ALIASES[raw];
    if (pa && (INTENT_PRODUCT_ANALYTICS as readonly string[]).includes(pa) && !productAnalytics.includes(pa)) productAnalytics.push(pa);
  }

  const rawPlatform = (params.get('intent_platform') ?? '').trim().toLowerCase();
  const platform = (INTENT_PLATFORMS as readonly string[]).includes(rawPlatform) ? (rawPlatform as IntentPlatform) : undefined;

  return { present: true, intent: { goal, channels, productAnalytics, ...(platform ? { platform } : {}), source: 'questionnaire' } };
}

export function stripIntentParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of INTENT_PARAM_KEYS) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}
